import type { Community } from '@communities/community/domain';
import { Card, Heading, Text } from '@soteria/design-system-ve';

import * as styles from './CommunityCard.css.ts';

interface CommunityCardProps {
  community: Community;
  isSelected: boolean;
  onClick: (community: Community) => void;
}

/**
 * Simple hash function to generate a consistent number from a string
 */
function hashString(str: string): number {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);

    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return Math.abs(hash);
}

/**
 * Get the background style for the community card
 */
function getBackgroundStyle(community: Community): React.CSSProperties {
  if (community.logoUrl) {
    return { backgroundImage: `url(${community.logoUrl})` };
  }

  // Randomly select from hero images 1, 5, 6, or 8 based on community ID
  const heroImages = [1, 5, 6, 8];
  const hash = hashString(community.id);
  const selectedHero = heroImages[hash % heroImages.length];

  return {
    backgroundImage: `url(/hero/hero-${selectedHero}.png)`,
  };
}

/**
 * Card component for displaying a community
 */
export function CommunityCard({ community, isSelected, onClick }: CommunityCardProps) {
  // Get the background style
  const backgroundStyle = getBackgroundStyle(community);

  // Get the description text
  const descriptionText = community.optionalMetadata?.description || 'No description available';

  return (
    <Card
      className={styles.cardContainer}
      elevation={isSelected ? 'high' : 'medium'}
      isSelected={isSelected}
      padding="none"
      width="full"
      onSelect={() => onClick(community)}
    >
      <div className={styles.communityImage} style={backgroundStyle}>
        {!community.logoUrl && <div className={styles.imageOverlay} />}
      </div>

      <div className={styles.contentContainer}>
        <Heading className={styles.communityTitle} level="h2" size="xl" weight={isSelected ? 'bold' : 'semibold'}>
          {community.name}
        </Heading>

        <Text className={styles.communityDescription} color="secondary" size="sm">
          {descriptionText}
        </Text>
      </div>
    </Card>
  );
}
